/**
 * Icon Font Utilities for OpenHASP Designer
 * Provides Material Icons support similar to LVGL/OpenHASP
 * 
 * IMPORTANT: OpenHASP firmware includes only the selected built-in icons.
 * To use additional icons, upload MaterialDesignIcons.ttf to your device.
 * See: https://www.openhasp.com/0.7.0/design/fonts/
 */

import { getBuiltInIconCharacter } from './openHaspBuiltinIcons';

let materialIconsMap = null;

/**
 * Load Material Icons codepoints from the codepoints file
 * @returns {Promise<Map>} Map of icon names to Unicode codepoints
 */
export async function loadMaterialIcons() {
    if (materialIconsMap) return materialIconsMap;

    try {
        const response = await fetch('/fonts/MaterialIcons-codepoints');
        const text = await response.text();
        
        materialIconsMap = new Map();
        
        // Parse the codepoints file
        // Format: icon_name codepoint (e.g., "home e88a")
        const lines = text.split('\n');
        for (const line of lines) {
            const trimmed = line.trim();
            if (!trimmed) continue;
            
            const parts = trimmed.split(/\s+/);
            if (parts.length >= 2) {
                const iconName = parts[0];
                const codepoint = parts[1];
                materialIconsMap.set(iconName, codepoint);
            }
        }
        
        return materialIconsMap;
    } catch (error) {
        console.error('Failed to load Material Icons:', error);
        materialIconsMap = new Map();
        return materialIconsMap;
    }
}

/**
 * Convert a Material Icon name to its Unicode character
 * @param {string} iconName - The Material Icon name (e.g., 'home', 'settings')
 * @returns {string} The Unicode character or empty string if not found
 */
export function getIconCharacter(iconName) {
    if (!materialIconsMap) {
        console.warn('Material Icons not loaded yet. Call loadMaterialIcons() first.');
        return '';
    }
    
    const codepoint = materialIconsMap.get(iconName);
    if (!codepoint) {
        console.warn(`Icon '${iconName}' not found in Material Icons`);
        return '';
    }
    
    // Convert hex codepoint to Unicode character
    return String.fromCodePoint(parseInt(codepoint, 16));
}

/**
 * Convert Material Icon name to Unicode escape notation (e.g., \uE88A)
 * @param {string} iconName - The Material Icon name
 * @returns {string} Unicode escape notation or empty string
 */
export function getIconUnicodeNotation(iconName) {
    if (!materialIconsMap) {
        console.warn('Material Icons not loaded yet. Call loadMaterialIcons() first.');
        return '';
    }
    
    const codepoint = materialIconsMap.get(iconName);
    if (!codepoint) {
        console.warn(`Icon '${iconName}' not found in Material Icons`);
        return '';
    }
    
    return `\\u${codepoint.toUpperCase()}`;
}

/**
 * Process text containing icon placeholders and convert to actual Unicode characters
 * Supports multiple formats:
 * 1. {{icon:home}} or {{icon:arrow-down}} - Icon placeholder format (OpenHASP names with hyphens)
 * 2. \uE88A - Direct Unicode escape notation (OpenHASP/LVGL style)
 * 
 * @param {string} text - Text containing icon placeholders or Unicode escapes
 * @returns {string} Text with icons replaced by Unicode characters
 */
export function processIconText(text) {
    if (!text || typeof text !== 'string') return text;
    
    let result = text;
    
    // Process {{icon:name}} format - try OpenHASP built-in icons first
    result = result.replace(/\{\{icon:([a-z_0-9-]+)\}\}/gi, (match, iconName) => {
        const lowerName = iconName.toLowerCase();
        // Try built-in icons first (supports hyphens)
        const builtInChar = getBuiltInIconCharacter(lowerName);
        if (builtInChar) return builtInChar;
        // Fallback to Material Icons (supports underscores)
        return getIconCharacter(lowerName) || match;
    });
    
    // Process \uXXXX format (OpenHASP/LVGL style)
    result = result.replace(/\\u([\dA-Fa-f]{4})/g, (match, code) => {
        return String.fromCodePoint(parseInt(code, 16));
    });
    
    return result;
}

/**
 * Get all available Material Icon names
 * @returns {string[]} Array of icon names
 */
export function getAllIconNames() {
    if (!materialIconsMap) {
        return [];
    }
    return Array.from(materialIconsMap.keys()).sort();
}

/**
 * Search for icons by name
 * @param {string} query - Search query
 * @param {number} limit - Maximum number of results (default: 50)
 * @returns {string[]} Array of matching icon names
 */
export function searchIcons(query, limit = 50) {
    if (!materialIconsMap || !query) {
        return [];
    }
    
    const lowerQuery = query.toLowerCase();
    const results = [];
    
    for (const iconName of materialIconsMap.keys()) {
        if (iconName.includes(lowerQuery)) {
            results.push(iconName);
            if (results.length >= limit) break;
        }
    }
    
    return results.sort();
}

/**
 * Check if an icon exists
 * @param {string} iconName - Icon name to check
 * @returns {boolean} True if icon exists
 */
export function hasIcon(iconName) {
    return materialIconsMap?.has(iconName) || false;
}

// Auto-load Material Icons when module is imported
loadMaterialIcons();

export default {
    loadMaterialIcons,
    getIconCharacter,
    getIconUnicodeNotation,
    processIconText,
    getAllIconNames,
    searchIcons,
    hasIcon
};
